import { useState, useEffect, useRef } from "react";
import {
  InputField,
  SelectField,
  TextareaField,
  ImageUploadField,
} from "../FormFields";
import PageLayout from "../PageFromLayout";
import FormSection from "../FormSection";
import Alert from "components/common/Alert";
import { validateForm } from "utils/validation";
import { getSpeakersByCompany } from "api/speaker.api";
import { getOrganizerById } from "api/company.api";
import { getImageUrl } from "utils/imageHelper";
import { validateImageFile } from "utils/common";

export default function ProgramForm({
  initialData,
  onSubmit,
  onBack,
  alert,
  setAlert,
}) {
  const [selectedOrganizer, setSelectedOrganizer] = useState(
    localStorage.getItem("company_id") || ""
  );
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    programType: "meeting", // Static Default: meeting
    title: "",
    programDate: "",
    startTime: "",
    description: "",
    speakers: [],
    image: null,
  });

  const [speakersList, setSpeakersList] = useState([]);
  const [dateRange, setDateRange] = useState({ min: "", max: "" });
  const [errors, setErrors] = useState({});
  const alertRef = useRef(null);
  const isEdit = Boolean(initialData);

  // Static Type Options
  const staticProgramTypeOptions = [
    { value: "meeting", label: "Meeting" },
    { value: "break", label: "Break" },
    { value: "other", label: "Other" },
  ];

  const isMeetingType = formData.programType === "meeting";
  const isBreakType = formData.programType === "break";
  const isOtherType = formData.programType === "other";

  // Show description field for both "meeting" and "other" types
  const hasDescription = isMeetingType || isOtherType;

  // Dynamic Validation Schema
  const schema = {
    programType: { required: true, label: "Program Type" },
    title: { required: true, label: "Title", minLength: 2 },
    programDate: { required: true, label: "Date" },
    startTime: { required: true, label: "Start Time" },
    description: { required: hasDescription, label: "Description" },
    speakers: { required: isMeetingType, label: "Speaker(s)" },
    image: { required: false, type: "image", label: "Break Image" },
  };

  // 1. Load Dropdowns & Company Boundaries
  useEffect(() => {
    if (!selectedOrganizer) return;

    const loadDropdownData = async () => {
      try {
        // Fetch Speakers
        const speakerRes = await getSpeakersByCompany(selectedOrganizer);
        const speakers = (speakerRes?.data?.data || speakerRes?.data || []).map(
          (item) => ({
            value: item._id || item.id,
            label: `${item.name} (${item.email})`,
          })
        );
        setSpeakersList(speakers);

        // Fetch Event Boundary Dates
        const companyRes = await getOrganizerById(selectedOrganizer);
        const companyData = companyRes.data?.data || companyRes.data || {};

        if (companyData.eventStartDate && companyData.eventEndDate) {
          const minDate = companyData.eventStartDate.split("T")[0];
          const maxDate = companyData.eventEndDate.split("T")[0];
          setDateRange({ min: minDate, max: maxDate });
        }
      } catch (err) {
        console.error("Failed to load program dropdowns:", err);
      }
    };

    loadDropdownData();
  }, [selectedOrganizer]);

  // 2. Populate Form State
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const userId = userData?.userId || userData?._id || "";

    if (initialData) {
      const populatedSpeakers = Array.isArray(initialData.speakers)
        ? initialData.speakers.map((s) => (typeof s === "object" ? s._id : s))
        : [];

      setFormData((prev) => ({
        ...prev,
        programType: initialData.programType?.toLowerCase() || "meeting",
        title: initialData.title || "",
        programDate: initialData.programDate
          ? new Date(initialData.programDate).toISOString().split("T")[0]
          : "",
        startTime: initialData.startTime || "",
        description: initialData.description || "",
        speakers: populatedSpeakers,
        image: initialData.image ? getImageUrl(initialData.image) : null,
        company_id: selectedOrganizer || initialData.company_id || "",
        created_by: initialData.created_by || userId,
        updated_by: userId,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        programType: "meeting",
        company_id: selectedOrganizer || "",
        created_by: userId,
        updated_by: userId,
      }));
    }
  }, [initialData, selectedOrganizer]);

  useEffect(() => {
    const handleOrganizerChange = (e) => {
      setSelectedOrganizer(e.detail);
    };

    window.addEventListener("companyChanged", handleOrganizerChange);
    return () =>
      window.removeEventListener("companyChanged", handleOrganizerChange);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleProgramTypeChange = (e) => {
    const selectedType = e.target.value;
    const allowsDescription = selectedType === "meeting" || selectedType === "other";

    setFormData((prev) => ({
      ...prev,
      programType: selectedType,
      description: allowsDescription ? prev.description : "",
      speakers: selectedType === "meeting" ? prev.speakers : [],
      image: selectedType === "break" ? prev.image : null,
    }));

    setErrors((prev) => ({
      ...prev,
      programType: "",
      description: "",
      speakers: "",
      image: "",
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const { isValid, error } = await validateImageFile(file, 2);

    if (!isValid) {
      setErrors((prev) => ({ ...prev, image: error }));
      return;
    }

    setErrors((prev) => ({ ...prev, image: "" }));
    setFormData((prev) => ({ ...prev, image: file }));
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    const validationErrors = await validateForm(formData, schema);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      if (alertRef.current) {
        alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      return;
    }

    setSubmitting(true);

    const uploadData = new FormData();
    uploadData.append("company_id", selectedOrganizer);
    uploadData.append("programType", formData.programType);
    uploadData.append("title", formData.title);
    uploadData.append("programDate", formData.programDate);
    uploadData.append("startTime", formData.startTime);

    if (hasDescription) {
      uploadData.append("description", formData.description);
    }

    if (isMeetingType) {
      uploadData.append("speakers", JSON.stringify(formData.speakers || []));
    }

    if (isBreakType && formData.image instanceof File) {
      uploadData.append("image", formData.image);
    }

    if (onSubmit) {
      await onSubmit(uploadData);
    }
    setSubmitting(false);
  };

  return (
    <PageLayout
      onBack={onBack}
      onSubmit={handleSubmitForm}
      submitText={isEdit ? "Update Program" : "Create Program"}
      loading={submitting}
    >
      <form onSubmit={handleSubmitForm} className="flex flex-col gap-6">
        {alert && (
          <div ref={alertRef} className="scroll-mt-[100px] px-6 pt-3">
            <Alert type="error" message={alert} onClose={() => setAlert("")} />
          </div>
        )}

        <FormSection title="Program Details">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <SelectField
              label="Program Type"
              name="programType"
              value={formData.programType}
              onChange={handleProgramTypeChange}
              options={staticProgramTypeOptions}
              required
              error={errors.programType}
            />

            <InputField
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter program title"
              error={errors.title}
              required
            />

            <InputField
              label="Program Date"
              name="programDate"
              type="date"
              value={formData.programDate}
              onChange={handleChange}
              min={dateRange.min}
              max={dateRange.max}
              error={errors.programDate}
              required
            />

            <InputField
              label="Start Time"
              name="startTime"
              type="time"
              value={formData.startTime}
              onChange={handleChange}
              error={errors.startTime}
              required
            />
          </div>
        </FormSection>

        {/* Meeting & Other Details Section */}
        {hasDescription && (
          <FormSection title={isOtherType ? "Additional Details" : "Meeting Details"}>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <TextareaField
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter description..."
                rows={3}
                error={errors.description}
                required
              />

              {/* Speakers dropdown remains strictly for Meeting type */}
              {isMeetingType && (
                <SelectField
                  label="Select Speaker(s)"
                  name="speakers"
                  value={formData.speakers}
                  onChange={(e) => {
                    const values = Array.isArray(e.target.value)
                      ? e.target.value
                      : [e.target.value];
                    setFormData((prev) => ({ ...prev, speakers: values }));
                    setErrors((prev) => ({ ...prev, speakers: "" }));
                  }}
                  options={speakersList}
                  isMulti={true}
                  isSearchable={true}
                  placeholder="Search and select speakers..."
                  required
                  error={errors.speakers}
                />
              )}
            </div>
          </FormSection>
        )}

        {/* Break Image Section */}
        {isBreakType && (
          <FormSection title="Break Media">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <ImageUploadField
                label="Break Image (Optional)"
                name="image"
                preview={
                  formData.image instanceof File
                    ? URL.createObjectURL(formData.image)
                    : typeof formData.image === "string"
                    ? formData.image
                    : ""
                }
                onUpload={handleFileChange}
                error={errors.image}
              />
            </div>
          </FormSection>
        )}
      </form>
    </PageLayout>
  );
}